<template>
    <div
        id="player-container"
        class="player-container"
        tabindex="0"
    >
        <button
            id="focusCatcher" 
            ref="focusCatcher" 
            class="player-container__button-focus" 
            @focusin="onFocus()"
            @focusout="onBlur()"
        />
        <div class="player-container__content">
            <hls-video-player 
                v-if="isHls" 
                ref="player"
                :key="hlsVideoPlayerKey"
                custom-event-name="stateChanged"
                :item-processor="itemProcessor"
                :subtitle="subtitles"
                :audio="audio"
                :volume="volume"
                :is-play-beginning="isPlayBeginning"
                :preferred-audio="preferredAudio"
                :is-available-descriptive-audio="isAvailableDescriptiveAudio"
                :is-seeking="isSeeking"
                :media-source="mediaSource"
                @ready="onPlayerReady"
                @error="onPlayerError"
                @set-subtitles="setSubtitles"
                @set-audio="setAudio"
                @retry="onRetryPlayer"
                @waiting="onWaiting"
                @canplay="onCanplay"
                @set-preferred-audio="setPreferredAudio"
                @hard-restart="hardRestart"
                @ended="onEnded"
                @pause="onPause"
                @playing="onPlaying"
                @timeupdate="onTimeupdate"
                @seeked="onSeeked"
                @click="onClickPlayer"
                @seeking="onSeeking"
                @error-507="onError507"
            />

            <dash-video-player 
                v-if="isDash" 
                ref="player"
                :key="dashVideoPlayerKey"
                custom-event-name="stateChanged"
                :item-processor="itemProcessor"
                :subtitle="subtitles"
                :audio="audio"
                :volume="volume"
                :is-play-beginning="isPlayBeginning"
                :preferred-audio="preferredAudio"
                :is-available-descriptive-audio="isAvailableDescriptiveAudio"
                :is-seeking="isSeeking"
                :media-source="mediaSource"
                :token="drmToken"
                @ready="onPlayerReady"
                @error="onPlayerError"
                @set-subtitles="setSubtitles"
                @set-audio="setAudio"
                @retry="onRetryPlayer"
                @waiting="onWaiting"
                @canplay="onCanplay"
                @set-preferred-audio="setPreferredAudio"
                @hard-restart="hardRestart"
                @ended="onEnded"
                @pause="onPause"
                @playing="onPlaying"
                @timeupdate="onTimeupdate"
                @seeked="onSeeked"
                @click="onClickPlayer"
                @seeking="onSeeking"
                @error-507="onError507"
            />
        </div>

        <loader v-if="isLoaderShown" class="player-container__loader" />

        <panel-top
            v-if="showControls && !featureGuideControlsEnabled"
            :play-item-name="playItemName"
            :channel-number="channelNumber"
            :channel-image="formatedChannelImage"
            :channel-call-sign="channelCallSign"
            :channel-name="channelName"
        />

        <episode-end-screen 
            v-if="isVisibleEpisodeEndScreen"
            ref="episodeEndScreenRef"
            :images="currentProgram?.images ?? null"
            :advisory="currentProgram?.advisory ?? null"
            :player-time="mediaCurrentTime"
            :startime-for-episode-end-screen="startimeForEpisodeEndScreen"
            :has-btn-replay="isVisibleBtnShowReplay"
            :has-btn-next-episode="isVisibleBtnNextEpisode"
            @close-player="closePlayer"
            @on-click-replay="replay"
            @on-click-next-episode="goNextEpisode"
            @ended-progress="endedEpisodeEndScreen"
        />

        <guide-controls 
            v-if="showControls && featureGuideControlsEnabled"
            ref="guideControlsRef"
            :channel-image="channelImage"
            :channel-number="channelNumber"
            :play-item="currentProgram ?? currentChannel"
            :volume="volume"
            :is-showing-subtitles="isShowingSubtitles"
            :is-modal-closed="isModalClosed"
            :is-play-beginning="isPlayBeginning"
            :is-playing-channel="playingIsChannel"
            @restart="onReset"
            @go-live="onGoLive"
            @close-player="closePlayer"
            @show-subtitles="onShowSubtitles"
            @refresh-action-time="refreshActionTime"
            @set-next-channel="onNextChannelPressed"
            @set-prev-channel="onPrevChannelPressed"
        />

        <controls-panel
            v-if="!featureGuideControlsEnabled && showControls && !playingIsChannel"
            ref="controlsPanelRef"
            v-model:isBifEnabled="isBifEnabled"
            :play-item="currentProgram"
            :show-controls="showControls"
            :item-processor="itemProcessor"
            :is-playing="isPlaying"
            :current-media-time="mediaCurrentTime"
            :current-time-position="currentTimePosition"
            :is-modal-closed="isModalClosed"
            :watched-range-available-f-f="watchedRangeAvailableFF"
            :watched-range-available-r-w="watchedRangeAvailableRW"
            :prev-episode="prevEpisode"
            :next-episode="nextEpisode"
            :is-dash="isDash"
            :show-subtitles-and-audio-btn="isShownSubtitlesAndAudioBtn"
            :watched-range="getSegmentsWR"
            :is-seeking="isSeeking"
            @refresh-action-time="refreshActionTime"
            @play="onPlay"
            @hide="onHide"
            @seek="onSeek"
            @restart="onReset"
            @go-live="onGoLive"
            @show-subtitles="onShowSubtitles"
            @next-episode="onNextEpisode"
            @prev-episode="onPrevEpisode" 
            @check-is-favorite="getItemProperties"
            @check-is-favorite-channel="getItemPropertiesChannel"
            @close-player="closePlayer"
            @rewind="onRewindPressed"
            @fast-forward="onFastForwardPressed"
            @list-item-selected="itemsSelected"
            @hover-on-time="hoverOnTime"
            @seek-to-hovered-time="seekToHoveredTime"
            @show-function-unavailable-tooltip="showFunctionUnavailableTooltip"
        />
        <custom-modal
            v-if="dialogModal.isShown"
            :title="dialogModal.title"
            :message="dialogModal.message"
            :buttons="dialogModal.buttons"
            :name="dialogModal.name"
            @on-btn-click="onModalClick"
        />
        <subtitle-modal 
            v-if="showSubtitleModal"
            :show-modal="showSubtitleModal"
            :all-subtitles="allSubtitles"
            :all-audio="allAudio"
            :available-audio="isAvailableDescriptiveAudio"
            @on-modal-click="closeModal" 
        />
        <stream-retry-modal
            v-if="retryModal.isShow"
            :loading="isRetrying"
            @on-btn-click="handleRetryModalConfirm"
            @close="handleRetryClose"
        />
        <Teleport to="body">
            <guide-manager 
                v-if="isVisibilityGuideManager" 
                class="guide-manager_top"
                :channels="playerChannels" 
                @founded="findChannelSegmentByChannel"
            />
        </Teleport>
    </div>
</template>

<script setup lang="ts">
    import { type PropType, nextTick, type Ref, ref, watch, computed, onMounted, onBeforeUnmount, onBeforeMount } from "vue";
    import type { IVideoPlayer, VideoPlayerEvent } from '@/components/Player/types';
    import { MediaFormatId } from './types/enums/MediaFormatId';
    import { MediaSourceParams } from './types/enums/MediaSourceParams';
    import { 
        PlayItem,
        TrickModeRestriction,
        type Channel,
        type PlayerTracks,
        PlayItemType,
        ContentItemProps,
        type OnDemandItem,
        isTabletDevice,
        type UserAlert,
        type MediaStream,
        TvQualifier,
        type ProgramGuideItem,
        type ContentProperties,
        type PerformanceTrace,
    } from '@ca.vmedia.tv/domains';
    import { ContentType } from '@ca.vmedia.tv/swagger-api';
    import type {ControlsPanelRef, GuideControlsRef, EpisodeEndScreenRef} from "./components/types";
    import {Strings} from "@/locales/types/enums/strings";
    import {PlayItemRestrictionsProcessor} from "./types/PlayItemRestrictionsProcessor";
    import { useApplicationStore } from "@/stores/Application";
    import { Logger, TimeUtils, UrlSearchParamUtils } from '@/utils';
    import { AppConstants } from "@/common";
    import { linkItems } from "@/components/Header/types";
    import { type OnDemandCardData, type ProgramCardData, type EpisodeCardData, ChannelLiveCardData} from "@/components/Cards/types";
    import { useChannelsStore } from "@/stores/Channels";
    import { useOnline } from '@vueuse/core';
    import type { ICustomModalState } from '@/composables/globalModals/types'
    import { useI18n } from "vue-i18n";
    import { useParentControl } from "@/composables/parentControl/useParentControl"
    import type { ExposeInterface } from "./types";
    import { useMedia } from "@/stores/Media";
    import { GetItemPropertiesRequest, StreamRequest } from "@/stores/Media/types";
    import { storeToRefs } from 'pinia';
    import { useParentControlStore } from "@/stores/ParentControl";
    import { useUserSettingsStore } from "@/stores/UserSettings";
    import { usePlayerStore } from "@/stores/Player";
    import { useClientMessagesStore } from "@/stores/ClientMessages";
    import { RetryModalTypes, type IRetryModal } from "./types/enums/RetryTypes";
    import { useFetchStream } from "@/composables/player/useFetchStream";
    import { useHomePageStore } from "@/stores/HomePage";
    import { useFavoriteChannelsStore } from "@/stores/FavoriteChannels";
    import { ButtonsIds as BtnsIdsEpisodeEndScreen } from "./components/EpisodeEndScreen/types";
    import { useWatchedRange } from "@/composables/player/WatchedRange/useWatchedRange";
    import type { AxiosError } from "axios";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { useDetailShowPageStore } from "@/stores/DetailShowPage";
    import { useDetailProgramPageStore } from "@/stores/DetailProgramPage";
    import { Param as AnalyticsParam } from "@/plugins/analytics/interfaces/analytics.service.interface";
    import { ScreenName } from "@/common/types/screen-names";
    import { useGenresStore } from "@/stores/Genres";

    const DetailProgramPage = useDetailProgramPageStore();
    const {
        getProgramsProperties,
    } = storeToRefs(DetailProgramPage);

    const DetailShowPageStore = useDetailShowPageStore();
    const {
        getEpisodesProperties: _episodesPropertiesDetailPage,
        getEpisodes: getEpisodesForDetailPage,
    } = storeToRefs(DetailShowPageStore);

    const DetailPageStore = useDetailPageStore();
    const {
            setViewedDetailItems
    } = DetailPageStore;

    const FavoriteChannelsStore = useFavoriteChannelsStore();
    const {
        fetchFavChannelsIds,
    } = FavoriteChannelsStore;

    const HomePageStore = useHomePageStore();
    const {
        getEpisodesProperties: _episodesPropertiesHomePage,
        getEpisodes: getEpisodesForHomePage,
    } = storeToRefs(HomePageStore);
    const {
        fetchRecentChannels: fetchRecentChannelsForHomePage,
        fetchEpisodes
    } = HomePageStore;

    const ClientMessagesStore = useClientMessagesStore();
    const {
        isModalVisible: isModalMessagesShown,
    } = storeToRefs(ClientMessagesStore);

    const ApplicationStore = useApplicationStore();
    const { 
        addSelectedIdProgram, 
        deleteSelectedIdProgram, 
        setTooltipeShowing, 
        setTooltipeMessage,
        showGlobalMaintenanceModal,
        showPlayerMaintenanceModal,
        setModal507Message,
        setIsShown507ErrorModal,
    } = ApplicationStore;

    const {
        getIsRequestIsLocked,
        getIsPlayerMaintenanceModalShow,
        getIsGlobalMaintenanceModalShow,
        getIs507ErrorModalShow,
        getModal507Message,
        getIsInternetModalShow,
        getCurrentSelectedIdProgram
    } = storeToRefs(ApplicationStore);

    const ChannelsStore = useChannelsStore();
    const {
        getAvailableChannels: playerChannels,
    } = storeToRefs(ChannelsStore);

    const PlayerStoreModule = usePlayerStore();
    const {
        setPlayFromBeginning,
        setUpdateProgram,
        setIsShownAlertModal,
        setVideoResume,
        setSubtitles: setSubtitlesStores,
        setAudio: setAudioStores,
        setPlayerAvailability,
        setToInitialState,
        setPlayerClosure,
        fetchNpasMessage,
        startPlayingChannel,
        fetchLiveProgram,
        updateCurrentProgram,
        playbackProgress,
        updateCurrentProgramByTime,
        startPlayingProgram,
        fetchRelatedContent,
        setVisibleDescription
    } = PlayerStoreModule;
    const {
        currentProgram,
        isPlayBeginning,
        getCurrentChannel,
        liveProgram,
        playingIsChannel,
        subtitles,
        audio,
        isPlayerClosure,
        alertsRetrieved,
        videoResume,
        isVisibleDescription,
        getVolume: volume,
        getEpisodes,
        isAlertModalVisible
    } = storeToRefs(PlayerStoreModule);

    const MediaStoreModule = useMedia();
    const {
        fetchItemProperties,
    } = MediaStoreModule;
    const {
        getStreamPosition: streamPosition,
    } = storeToRefs(MediaStoreModule);

    const UserSettingsStoreModule = useUserSettingsStore();
    const {
        getEnabledAutoplay: isEnableAutoplay
    } = storeToRefs(UserSettingsStoreModule);
    const ParentControlStoreModule = useParentControlStore();
    const {
        SetIncorrectAdultPin,
        ShowParentModal,
        SetTargetWidgetAdultControl,
    } = ParentControlStoreModule;
    const {
        getIsModalParentVisible: isTypeModeEnabledPin,
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl,
    } = storeToRefs(ParentControlStoreModule);

    const GenresStoreModule = useGenresStore();
    const {
        getTopGenres,
    } = storeToRefs(GenresStoreModule);
    
    const props = defineProps({
        playItem: {
			type: Object as PropType<PlayItem>,
        	required: true
      	},
        playItemType: {
            type: String,
            required: true
        },
    });

    const router = useRouter();
    const slowInternetTimeout = 30000;
    const errorChannelId ="Channel id is not defined";

    const { fetchStream, retryFetchStream, isRetrying, fetchStreamErrorHandling, error507Handling } = useFetchStream();
    const emit = defineEmits(['emulateKeyEnter', 'setVisibilityToBtnShowReplay', 'exit-play', 'showSubtitleModal', 'setVisibilityToBtnNextEpisode', 'showControls', 'showRetryModal']);
    const { $fire, $navigator, $domains, $analytics, $services } = useNuxtApp();
    const { guideConfig, isTimeFormat12 } = useAppConfig();
    const { t, locale } = useI18n();
    const {
        featureGuideControlsEnabled,
        featureChannelLogoOriginalEnabled
    } = guideConfig;
    const tracePlayerEventReset = $fire.trace($fire.performance, 'PLAYER_RESTART');
    const tracePlayerEventLive = $fire.trace($fire.performance, 'PLAYER_LIVE');
    let tracePlayer: PerformanceTrace | null = $fire.trace($fire.performance, 'PLAYER');
    const { setCurrentTimes, needsUnlockPin, checkParentControlCard } = useParentControl();
    const itemContentType: Ref<ContentType | null> = ref(null);
    const channelId: Ref<number | null> = ref(null);
    const currentItemId: Ref<number | null> = ref(null);
    const mediaCurrentTime: Ref<number> = ref(0);
    const player: Ref<IVideoPlayer | null> = ref(null);
    const showControls: Ref<boolean> = ref(false);
    const isPlaying: Ref<boolean> = ref(false);
    const channelImage: Ref<string | null> = ref(null);
    const hideTimeout: Ref<ReturnType<typeof setInterval> | null> = ref(null);
    const rewindTimeout: Ref<ReturnType<typeof setInterval> | null> = ref(null);
    const skipPosition: Ref<number> = ref(0);
    const isStarted: Ref<boolean> = ref(false);
    const handle: Ref<ReturnType<typeof setInterval> | null> = ref(null);
    const handleLocalStorage: Ref<ReturnType<typeof setInterval> | null> = ref(null);
    const isLive: Ref<boolean> = ref(false);
    const showSubtitleModal: Ref<boolean> = ref(false);
    const isModalClosed: Ref<boolean> = ref(false);
    const playerName: Ref<ScreenName | null> = ref(null);
    const allSubtitles: Ref<Array<PlayerTracks> | null> = ref(null);
    const allAudio: Ref<Array<PlayerTracks> | null> = ref(null);
    const autoTransitionNextProgram: Ref<boolean> = ref(false);
    const startTimePlayerUXSec: Ref<number> = ref(0);
    const dispatchAvailabilityRange: Ref<boolean> = ref(true);
    const isVisibleBtnNextEpisode: Ref<boolean> = ref(false);
    const isVisibleBtnShowReplay: Ref<boolean> = ref(false);
    const isVisibleEpisodeEndScreen = computed((): boolean => !showControls.value && (isVisibleBtnShowReplay.value || isVisibleBtnNextEpisode.value));
    
    const isTransitionEpisode: Ref<boolean> = ref(false);
    const transitionEpisode: Ref<OnDemandItem | null> = ref(null);
    
    const handleNpasMessage: Ref<ReturnType<typeof setInterval> | null> = ref(null);
    const isRegKeyBackListeners: Ref<boolean> = ref(false);
    const dialogModal: Ref<ICustomModalState> = ref({
        isShown: false,
        title: null,
        message: null,
        name: null,
        buttons: null
    });
    const focusCatcher: Ref<HTMLButtonElement | null> = ref(null);
    const guideControlsRef: Ref<GuideControlsRef | null> = ref(null);
    const episodeEndScreenRef: Ref<EpisodeEndScreenRef | null> = ref(null);
    const controlsPanelRef: Ref<ControlsPanelRef | null> = ref(null);
    const retryModal: Ref<IRetryModal> = ref({
        isShow: false,
        type: RetryModalTypes.STREAM
    });
    const isWaitingLoader: Ref<boolean> = ref(false);
    const preferredAudio: Ref<string> = ref("");
    let waitingTimer: ReturnType<typeof setTimeout> | null = null;
    const hlsVideoPlayerKey: Ref<number> = ref(0);
    const dashVideoPlayerKey: Ref<number> = ref(0);
    const isFirstInitPlayer: Ref<boolean> = ref(true);
    const stream: Ref<MediaStream | null> = ref(null);
    const hoveredTime: Ref<number> = ref(0);
    const isSeeking = ref(false);
    const pendingChannelIndex: Ref<number | null> = ref(null);
    const name = "Player";

    const isLoaderShown = computed((): boolean => !isLoaded.value || isWaitingLoader.value);
    const formatedChannelImage = computed((): string => channelImage.value ?? '');
    const startimeForEpisodeEndScreen = computed((): number => currentProgramEndTimeSec.value - AppConstants.durationForEpisodeEndScreen);
    const isProgram = computed((): boolean => props.playItemType !== PlayItemType.Channel);
    const isPlayChannel = computed((): boolean => props.playItemType === PlayItemType.Channel);

    const isDash = computed((): boolean => stream.value !== null && stream.value.mediaFormatId === MediaFormatId.DASH);
    const isHls = computed((): boolean => stream.value !== null && stream.value.mediaFormatId !== MediaFormatId.DASH);
    const mediaSource = computed((): string | null => stream.value?.url ?? null);
    const drmToken = computed((): string | null => stream.value?.drm?.token ?? null);
    const restrictions = computed((): TrickModeRestriction => stream.value?.trickModeRestriction ?? TrickModeRestriction.None);

    const currentChannel = computed((): Channel | null => getCurrentChannel.value ?? playerChannels.value.find(item => item.id === currentProgram.value?.recentAirTime?.channelId) ?? null);

    const isRestartingStream: Ref<boolean> = ref(false);

    const isVisibilityGuideManager  = computed((): boolean => !isTypeModeEnabledPin.value && (isLive.value || isVCloud.value || isRestartingStream.value));

    const itemProcessor = computed((): PlayItemRestrictionsProcessor | null => {
        if (!currentProgram.value) {
            return null;
        }

        return new PlayItemRestrictionsProcessor(currentProgram.value, restrictions.value, isRestartingStream.value, isTimeFormat12, currentChannel.value, locale.value);
    });

    const { setWatchedRange, getWatchedRange, clearWatchedRange, availabilityFF, availabilityRW, getSegmentsWR } = useWatchedRange(itemProcessor);

    const currentTimePosition = computed((): number => {
        if (skipPosition.value > 0) {
            return skipPosition.value;
        }
        return mediaCurrentTime.value;
    });
    const streamLoaded = computed((): boolean => !!stream.value);
    const isLoaded = computed((): boolean => streamLoaded.value && isStarted.value);
    const channelCallSign = computed((): string => currentChannel.value?.callSign ?? '');
    const channelName = computed((): string => currentChannel.value?.name ?? 'logo');
    const channelNumber = computed((): number => currentChannel.value?.accessNum ?? 0);
    const playItemName = computed((): string => itemProcessor.value?.title || '');
    const isShowingSubtitles = computed((): boolean => (subtitles.value === 'off' || subtitles.value === "") ? false : true);
    const maxDurationForResume = computed((): number => (currentProgram.value?.lengthInS ?? currentProgram.value?.length ?? 0) * 0.9);
    const isOffline = computed((): boolean => !useOnline().value);
    const fastForwardTime = computed((): number => {
        const endProgram = currentProgram.value?.lengthInS ?? currentProgram.value?.length ?? 0;
        const timePosition = currentTimePosition.value;

        const diffToEnd = endProgram - timePosition;
        if (diffToEnd < AppConstants.defaultSkipTime) {
            return diffToEnd;
        }
        return AppConstants.defaultSkipTime;
    });
    const rewindTime = computed((): number => {
        if (currentTimePosition.value < AppConstants.defaultSkipTime) {
            return -currentTimePosition.value + 1;
        }
        return -AppConstants.defaultSkipTime;
    });
    const startTimeSec = computed((): number => itemProcessor.value ? itemProcessor.value.startTimeSec : 0);
    const watchedRangeAvailableFF = computed((): boolean => {
        if (hasLive.value || itemProcessor.value?.liveIsPausedOnce) {
            return false;
        }
        return availabilityFF(startTimeSec.value + currentTimePosition.value);
    });
    const watchedRangeAvailableRW = computed((): boolean => {
        if (hasLive.value || itemProcessor.value?.liveIsPausedOnce) {
            return false;
        }
        return availabilityRW(startTimeSec.value + currentTimePosition.value);
    });
    const hasLive = computed((): boolean => isLive.value || !!itemProcessor.value?.hasLive);
    const hasPause = computed((): boolean => hasLive.value ? !!itemProcessor.value?.hasRestartAvailable && !!itemProcessor.value?.hasPause : !!itemProcessor.value?.hasPause);
    const nextEpisode = computed((): OnDemandItem | null => {
        const currentIndex = getEpisodes.value.findIndex((el: OnDemandItem) => el.episodeNum === currentProgram.value?.episodeNum);
        return getEpisodes.value[currentIndex+1] ?? null;
    });
    const prevEpisode = computed((): OnDemandItem | null => {
        const currentIndex = getEpisodes.value.findIndex((el: OnDemandItem) => el.episodeNum === currentProgram.value?.episodeNum);
        return getEpisodes.value[currentIndex-1] ?? null;
    });
    const indexCurrentChannel = computed((): number => playerChannels.value.findIndex(item => Number(item.id) === Number(channelId.value)));
    const indexNextChannel = computed((): number => (indexCurrentChannel.value !== playerChannels.value.length - 1) ? indexCurrentChannel.value + 1 : 0);
    const indexPrevChannel = computed((): number => indexCurrentChannel.value === 0 ? playerChannels.value.length - 1 : indexCurrentChannel.value - 1);
    const isVODSeries = computed((): boolean => itemProcessor.value?.isVODSeries ?? false);
    const isVCloud = computed((): boolean => itemProcessor.value?.isVCloud ?? false);
    const isRestart = computed((): boolean => itemProcessor.value?.isRestart ?? false);
    const isVODMovies = computed((): boolean => !!itemProcessor.value?.isCraveMovies || !!itemProcessor.value?.isVODMovies);
    const isVOD = computed((): boolean => isVODSeries.value || isVODMovies.value);
    const isRestartAvailable = computed((): boolean => !!itemProcessor.value?.hasRestartAvailable && PlayItem.isLive(currentProgram.value));
    const fetchItemId = computed((): number => {
        if (isLive.value || PlayItem.isLive(props.playItem)) {
            return channelId.value ?? 0;
        } else {
            return currentItemId.value ?? 0;
        }
    });
    const channelIsDescriptiveAudio = computed((): boolean => {
        const defaultValue = false;
        if (playingIsChannel.value) {
            return currentChannel.value?.isDescriptiveAudio || defaultValue;
        }
        if (!currentProgram.value || !currentProgram.value.recentAirTime) {
            return defaultValue;
        }
        const channel = playerChannels.value.find(item => item.id === currentProgram.value?.recentAirTime?.channelId);
        if (channel?.isDescriptiveAudio) {
            return channel.isDescriptiveAudio || defaultValue;
        }
        return defaultValue;
    });
    const isProgramHasDescriptiveAudio = computed((): boolean => {
        if (!currentProgram.value) {
            return false;
        }
        return (currentProgram.value as ProgramGuideItem).Qualifiers?.includes(TvQualifier.Dvs) || false;
    });
    const isAvailableDescriptiveAudio = computed((): boolean => channelIsDescriptiveAudio.value && isProgramHasDescriptiveAudio.value);
    const isShownSubtitlesAndAudioBtn = computed((): boolean => isAvailableDescriptiveAudio.value || (!!allSubtitles.value && allSubtitles.value.length > 0));

    const vend = computed((): number => {
        if (!mediaSource.value) {
            return 0;
        }
        const params = UrlSearchParamUtils(mediaSource.value, [MediaSourceParams.VEND]);
        return params?.[MediaSourceParams.VEND] ? Number(params[MediaSourceParams.VEND]) : 0;
    });

    const currentTimeUnix = computed((): number => {
        return startTimeSec.value + currentTimePosition.value;
    });
    const currentProgramEndTimeSec = computed((): number => itemProcessor.value ? itemProcessor.value.endTimeSec : 0);

    const { getBif, convertSrcToBif, isBifEnabled } = useBifImage(currentTimePosition, hoveredTime, isVOD);

    const onFocus = () => {
        registerKeyListeners();
    };
    const onBlur = () => {
        unregisterKeyListeners();
    };
    const returningFocus = (isShowModal: boolean): void => {
        if (
            !isShowModal && (retryModal.value.isShow || getIsPlayerMaintenanceModalShow.value || getIsGlobalMaintenanceModalShow.value
            || isTypeModeEnabledPin.value || isModalMessagesShown.value || getIs507ErrorModalShow.value || getIsInternetModalShow.value)
        ) {
            return;
        } else if (!isShowModal) {
            focusCatcher.value?.focus();
        }
    };
    const showRetryModal = (type: RetryModalTypes): void => {
        retryModal.value.isShow = true;
        retryModal.value.type = type;
        emit('showRetryModal', true);
    };
    const hideStreamRetryModal = () => {
        retryModal.value.isShow = false;
        setTimeout(() => {
            emit('showRetryModal', false);
            returningFocus(retryModal.value.isShow);
        }, 500)
    };
    const processStream = async () => {
        await getItemProperties();
        if (isPlayBeginning.value) {
            isRestartingStream.value = true;
        }
        if (!playingIsChannel.value) {
            await getWatchedRange();
        }
    };
    const getStream = async (): Promise<void> => {
        if (getIsRequestIsLocked.value) {
            console.warn("Fetch stream request is locked");
            showGlobalMaintenanceModal(true);
            return;
        }
        try {
            if (stream.value) {
                stream.value = null;
            }
            stream.value = await fetchStream(new StreamRequest(fetchItemId.value!, itemContentType.value!, isRestartAvailable.value));
            await processStream();
        } catch (error) {
            fetchStreamErrorHandling(error, handleRetryStream);
        }
    };
    const increasePlayerKey = () => {
        if (isFirstInitPlayer.value) {
            isFirstInitPlayer.value = false;
            return;
        }
        if (isHls.value) {
            hlsVideoPlayerKey.value++;
        } else {
            dashVideoPlayerKey.value++;
        }
    };
    const fetchList = async () => {
        if (currentProgram.value === null && currentChannel.value === null) {
            throw new Error("Play item is null");
        }
        isStarted.value = false;
        increasePlayerKey();
        
        
        if (currentProgram.value && 'subImage' in currentProgram.value) {
            const subImage = currentProgram.value['subImage'] as string;
            if (featureChannelLogoOriginalEnabled) {
                channelImage.value = await $domains.imagesRepository.getChannelImageOriginal(props.playItem?.recentAirTime?.imagesChannel) || subImage;
            } else {
                channelImage.value = subImage || await $domains.imagesRepository.getWhiteChannelImage(props.playItem?.recentAirTime?.imagesChannel);
            }
        }
        const isEmptyProgram = currentProgram.value && currentProgram.value.id === null;

        if (isLive.value) {
            itemContentType.value = ContentType.LiveTV;
            channelId.value = currentProgram.value?.recentAirTime?.channelId ?? currentChannel.value!.id;
            playerName.value = ScreenName.LIVE_TV_PLAYER;
        } else if (isProgram.value) {
            if (PlayItem.isLive(props.playItem)  || isEmptyProgram) {
                isLive.value = true;
                itemContentType.value = ContentType.LiveTV;
                channelId.value = currentProgram.value?.recentAirTime?.channelId ?? 0;
                playerName.value = ScreenName.LIVE_TV_PLAYER;
            } else if (currentProgram.value && PlayItem.isVCloud(currentProgram.value, currentChannel.value)) {
                itemContentType.value = ContentType.DVR;
                channelId.value = currentProgram.value.recentAirTime?.channelId ?? 0;
                currentItemId.value = currentProgram.value.recentAirTime?.id ?? 0;
                playerName.value = ScreenName.VCLOUD_PLAYER;
            } else {
                itemContentType.value = currentProgram.value?.contentType ?? null;
                currentItemId.value = currentProgram.value?.id ?? 0;
                playerName.value = ScreenName.VOD_PLAYER;
            }
        } else {
            itemContentType.value = ContentType.LiveTV;
            channelId.value = currentProgram.value?.recentAirTime?.channelId ?? 0;
            currentItemId.value = props.playItem.id;
            playerName.value = ScreenName.LIVE_TV_PLAYER;
            isLive.value = true;
        }
        
        if (playingIsChannel.value && currentChannel.value) {
            itemContentType.value = ContentType.LiveTV;
            channelImage.value = currentChannel.value.image;
            isLive.value = true;
        }

        await getStream();
    };
    const handleRetryStream = async (): Promise<void> => {
        try {
            if (stream.value) {
                stream.value = null;
            }
            const _stream = await retryFetchStream(new StreamRequest(fetchItemId.value!, itemContentType.value!, isRestartAvailable.value));
            hideStreamRetryModal();
            if (_stream) {
                stream.value = _stream;
                await processStream();
            }
        } catch (err) {
            showRetryModal(RetryModalTypes.STREAM);
        }
    };
    const handleRetryPlayer = () => {
        try {
            hideStreamRetryModal();
            player.value?.restart({
                mediaSource: mediaSource.value!,
                token: drmToken.value
            });
        } catch (e) {
            console.error("Unexpected error", e, `${isHls.value ? `hls=` : `dash=` }`, player.value);
        }
    };
    const handleRetryModalConfirm = () => {
        if (retryModal.value.type === RetryModalTypes.STREAM) {
            void handleRetryStream();
        } else if (retryModal.value.type === RetryModalTypes.PLAYER) {
            handleRetryPlayer();
        }
    };
    const handleRetryClose = (): void => {
        closePlayer();
        hideStreamRetryModal();
    };
    const onRetryPlayer = (): void => {
        showRetryModal(RetryModalTypes.PLAYER);
    };
    const onError507 = (): void => {
        //TODO: We need to make text for error more specific (VTA2-3194)
        void error507Handling(showGeneralErrorMessage);
    };

    const clearTimerStreamProgress = () => {
        if (handle.value) {
            clearInterval(handle.value!);
        }
        if (handleLocalStorage.value) {
            clearInterval(handleLocalStorage.value!);
        }
    };
    const toggleShowControls = () => {
        if (!dialogModal.value.isShown) {
            showControls.value = true;
            refreshActionTime();
        }
    };
    const focusControls = () => {
        if (showControls.value) {
            if (featureGuideControlsEnabled) {
                guideControlsRef.value?.focusDefault();
            } else {
                controlsPanelRef.value?.focusDefault();
            }
        }
    };
    const confirmStop = () => {
        dialogModal.value = {
            isShown: true,
            title: t(Strings.PLAYER_CONFIRM_STOP_PLAYING_TITLE).toString(),
            message: t(Strings.PLAYER_CONFIRM_STOP_PLAYING_MSG).toString(),
            buttons:  [
                t(Strings.SETTINGS_ACCOUNT_MODAL_POSITIVE).toString(),
                t(Strings.SETTINGS_ACCOUNT_MODAL_NEGATIVE).toString()
            ],
            name: "modalPlaying",
        }
    };
    const showGeneralErrorMessage = () => {
        dialogModal.value = {
            isShown: true,
            title: t(Strings.PLAYER_CONTENT_NOT_AVAILABLE_TITLE).toString(),
            message: t(Strings.PLAYER_CONTENT_NOT_AVAILABLE_MSG).toString(),
            buttons:  [
                t(Strings.BACK_BUTTON).toString()
            ],
            name: "generalError",
        }
    };

    const showMaintenanceModal  = () => {
        dialogModal.value = {
            isShown: true,
            title: t(Strings.MAINTENANCE_MODAL_TITLE).toString(),
            message: t(Strings.MAINTENANCE_MODAL_PLAYER_MESSAGE).toString(),
            buttons: [
                t(Strings.MAINTENANCE_MODAL_PLAYER_BUTTON).toString(),
            ],
            name: 'playerMaintenance',
        }
    };

    const show507ErrorModal = () => {
        dialogModal.value = {
            isShown: true,
            title: "",
            message: getModal507Message.value,
            buttons: [
                t(Strings.BACK_BUTTON).toString(),
            ],
            name: '507Error',
        }
    };

    const close507ErrorModal = () => {
        dialogModal.value.isShown = false;
        setIsShown507ErrorModal(false);
        setModal507Message('');
        emit("exit-play");
    };

    const onModalClick = async (answer: string) => {
        if (dialogModal.value.name === 'playerMaintenance') {
            focusControls();
            showPlayerMaintenanceModal(false);
        } else if (dialogModal.value.name === '507Error') {
            close507ErrorModal();
        } else if (answer === t(Strings.SETTINGS_ACCOUNT_MODAL_POSITIVE).toString()) {
            emit("exit-play");
        } else if (answer === t(Strings.BACK_BUTTON).toString()) {
            emit("exit-play");
        } else {
            focusControls();
        }
        dialogModal.value.isShown = false;
    };
    const onPlayerReady = () => {
        isStarted.value = true;
    };
    const onSeeking = (): void => {
        isSeeking.value = true;
    };
    const onSeeked = (): void => {
        skipPosition.value = 0;
        isSeeking.value = false;
    };
    const onTimeupdate = (mediaTimeSec: number): void => {
        mediaCurrentTime.value = mediaTimeSec;
    };
    const onPlaying = (): void => {
        isPlaying.value = true;
    };
    const onPause = (): void => {

        isPlaying.value = false;

        if (hasLive.value && itemProcessor.value && !itemProcessor.value?.liveIsPausedOnce) {
            itemProcessor.value.liveIsPausedOnce = true;
        }
    };
    const onEnded = (): void => {
        //'autoplay = off' for VOD series
        if (isVODSeries.value) {
            updateNextEpisode();
        }

        if (isVODMovies.value) {
            closePlayer();
        }
    };
    const endedEpisodeEndScreen = (): void => {
        updateNextEpisode();
    };
    const onPlayerError = (_eventObject: VideoPlayerEvent) => {
        if (!isOffline.value) {
            //TODO: We need to make text for error more specific (VTA2-3194)
            showGeneralErrorMessage();
        }
    };
    const onHide = () => {
        showControls.value = false;
        showSubtitleModal.value = false;
    };
    const onPlay = (): void => {
        if(!isLoaded.value) {
            return;
        }
        
        refreshActionTime();
        if (hasPause.value) {
            if (player.value?.isPaused()) {
                player.value?.resume()?.catch((error) => {
                    console.error("Error resuming video at onPlay:", error);
                });
            } else {
                setWatchedRange(startTimePlayerUXSec.value, currentTimeUnix.value).catch((error) => {
                    console.error("Error setting watched range at onPlay:", error);
                });
                player.value?.pause();
            }
        }
    };
    const onSeek = (seconds: number) => {
        if (seconds > 0 && itemProcessor.value && !itemProcessor.value?.hasFastForward && !watchedRangeAvailableFF.value) {
            return;
        }
        if (seconds < 0 && itemProcessor.value && !itemProcessor.value?.hasRewind && !watchedRangeAvailableRW.value) {
            return;
        }
        refreshActionTime();
        
        if (player.value && skipPosition.value === 0) {
            skipPosition.value = player.value.getCurrentTime();
        }

        skipPosition.value += seconds;
        if (skipPosition.value < 0) {
            skipPosition.value = 0;
        }
        refreshSkipTime();
    };
    const onPlayPressed = () => {
        if (!hasPause.value) {
            showFunctionUnavailableTooltip();
            return;
        }
        if (player.value?.isPaused()) {
            onPlay();
        }
    };
    const onPausePressed = () => {
        if (!hasPause.value) {
            showFunctionUnavailableTooltip();
            return;
        }
        if (!player.value?.isPaused()) {
            onPlay();
        }
    };
    const onRewindPressed = () => {
        if (watchedRangeAvailableRW.value) {
            refreshActionTime();
            onSeek(rewindTime.value);
            return;
        }
        if (!itemProcessor.value?.hasRewind || itemProcessor.value?.hasLive) {
            showFunctionUnavailableTooltip();
            return;
        }
        refreshActionTime();
        onSeek(rewindTime.value);
    };
    const onFastForwardPressed = () => {
        if (watchedRangeAvailableFF.value) {
            refreshActionTime();
            onSeek(fastForwardTime.value);
            return;
        }
        if (!itemProcessor.value?.hasFastForward || itemProcessor.value?.hasLive) {
            showFunctionUnavailableTooltip();
            return;
        }
        refreshActionTime();
        onSeek(fastForwardTime.value);
    };
    const seekToHoveredTime = () => {
        refreshActionTime();
        refreshSkipTime(hoveredTime.value);
    };
    const showFunctionUnavailableTooltip = () => {
        setTooltipeMessage(t(Strings.PLAYER_FUNCTION_UNAVAILABLE).toString());
        setTooltipeShowing({visible: true});
    };
    const switchChannel = async (indexChannel: number) => {
        try {
            const channel = playerChannels.value[indexChannel];
            if (!channel?.id) {
                throw new Error(`${errorChannelId} at switchChannel: ${JSON.stringify(
                    {
                        indexChannel,
                        programId: currentProgram.value?.id,
                        programName: currentProgram.value?.name,
                        recentAirTime: currentProgram.value?.recentAirTime,
                        contentType: [currentProgram.value?.contentType, itemContentType.value],
                        channelId: currentChannel.value?.id,
                        channelName: currentChannel.value?.name,
                    }
                )}`);
            }
            const program = await $services
                .guide
                .getChannelProgramByTime(channel.id, TimeUtils.dateNow());
            if (!program) {
                throw new Error(`Program is not found in channel at switchChannel: ${JSON.stringify(
                    {
                        indexChannel,
                        channelId: channel.id,
                        channelName: channel.name,
                        currentChannelId: currentChannel.value?.id,
                        currentChannelName: currentChannel.value?.name,
                        currentProgramName: currentProgram.value?.name,
                        currentRecentAirTime: currentProgram.value?.recentAirTime,
                        currentContentType: [currentProgram.value?.contentType, itemContentType.value],
                    }
                )}`);
            }

            const channelCardData = new ChannelLiveCardData(channel);
            startPlayingChannel(channelCardData, false).then(() => {
                player.value?.dispose().then(async () => {
                    isLive.value = true;
                    void router.push(linkItems.player.routerPath);
                    
                    await fetchList();

                    startTimePlayerUXSec.value = Math.floor(Date.now()/1000);
                    isRestartingStream.value = false;

                    // TODO: wrap updateRecentChannels into condition: if back to home page; or remove it
                    updateRecentChannels(Number(channelCardData.getId()));
                }).catch((error) => {
                    throw error;
                });
            }).catch((error) => {
                throw error;
            });
        } catch (error) {
            console.error(error);
            setTooltipeMessage(t(Strings.PLAYER_ERROR_SWITCH_CHANNEL).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const checkChannelParentalControl = async (channelIndex: number): Promise<void> => {
        try {
            if (isTypeModeEnabledPin.value) {
                return;
            }
            const channel = playerChannels.value[channelIndex];
            if (!channel) {
                return;
            }

            const channelCardData = new ChannelLiveCardData(channel);
            const event = {
                lineId: AppConstants.shelfKeys.playerChannelsCards,
                item: channelCardData,
                componentName: channelCardData.getComponentName(),
                genre: null,
            };

            const isAllow = await checkParentControlCard(event);
            if (isAllow) {
                await switchChannel(channelIndex);
            } else {
                pendingChannelIndex.value = channelIndex;
                ShowParentModal();
                SetTargetWidgetAdultControl(name);
                const focusId = document.activeElement?.id;
                addSelectedIdProgram(focusId ?? '');
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const onNextChannelPressed = () => {
        if (isVOD.value) {
            return;
        }
        if (isLoaded.value) {
            refreshActionTime();
            void checkChannelParentalControl(indexNextChannel.value);
        }
    };
    const onPrevChannelPressed = () => {
        if (isVOD.value) {
            return;
        }
        if (isLoaded.value) {
            refreshActionTime();
            void checkChannelParentalControl(indexPrevChannel.value);
        }
    };
    const onLeftPressed = () => {
        refreshActionTime();
        if (showControls.value) {
            controlsPanelRef.value?.onLeftPressed();
        }
    };
    const onRightPressed = () => {
        refreshActionTime();
        if (showControls.value) {
            controlsPanelRef.value?.onRightPressed();
        }
    };
    const onRewind = () => {
        refreshActionTime();
        if (showControls.value) {
            controlsPanelRef.value?.onRewind();
        }
    };
    const onFastForward = () => {
        refreshActionTime();
        if (showControls.value) {
            controlsPanelRef.value?.onFastForward();
        }
    };
    const onReset = async () => {
        try {
            tracePlayerEventReset.start();
            Logger.captureLog('PLAYER_RESTART start');
        } catch (e){
            Logger.captureLog(e);
        }

        if (player.value) {
            void setProgress();
            startTimePlayerUXSec.value = startTimeSec.value;
            isRestartingStream.value = true;
            isLive.value = false;
            refreshActionTime();
            player.value.setCurrentTime(0);
        }
        if (tracePlayerEventReset) {
            try {
                tracePlayerEventReset.stop();
                Logger.captureLog(`PLAYER_RESTART stop ${tracePlayerEventReset.durationUs / 1000000} sec`);
            } catch (e){
                Logger.captureError(e as Error);
            }
        }
    };
    const startLiveProgram = async (id?: string): Promise<void> => {
        try {
            const channelCurrent = currentChannel.value;
            if (!channelCurrent?.id) {
                throw new Error(`${errorChannelId}: ${JSON.stringify(
                    {
                        recentAirTime: currentProgram.value?.recentAirTime,
                        contentType: [currentProgram.value?.contentType, itemContentType.value],
                        channelId: currentProgram.value?.recentAirTime?.channelId,
                    }
                )}`);
            }

            await fetchLiveProgram({
                channelId: channelCurrent?.id
            });

            const liveProgramCurrentScheduleId = liveProgram.value?.recentAirTime?.id;
            if (!liveProgramCurrentScheduleId) {
                throw new Error(`LiveProgram.recentAirTime.id is not defined: ${JSON.stringify(
                    {
                        recentAirTime: currentProgram.value?.recentAirTime,
                        contentType: [currentProgram.value?.contentType, itemContentType.value],
                        channelId: channelCurrent?.id
                    }
                )}`);
            }
            
            const programCurrentScheduleId = currentProgram.value?.recentAirTime?.id;
            if (!programCurrentScheduleId) {
                throw new Error(`CurrentProgram.recentAirTime.id is not defined: ${JSON.stringify(
                    {
                        recentAirTime: liveProgram.value?.recentAirTime,
                        contentType: [liveProgram.value?.contentType, itemContentType.value],
                        channelId: channelCurrent?.id
                    }
                )}`);
            }

            setPlayFromBeginning(false);
            
            const advisory = liveProgram.value?.advisory ?? channelCurrent?.advisory;
            const currentProgramIsLiveProgram = liveProgramCurrentScheduleId === programCurrentScheduleId;

            if (!currentProgramIsLiveProgram && typeof advisory === 'number' && needsUnlockPin(advisory)) {
                ShowParentModal();
                if (id) {
                    addSelectedIdProgram(id);
                }
            } else {
                try {
                    tracePlayerEventLive.start();
                    Logger.captureLog('PLAYER_LIVE start');
                } catch (e){
                    Logger.captureLog(e as AxiosError | Error);
                }

                await setProgress();
                clearWatchedRange();
                isRestartingStream.value = false;
                await updateCurrentProgram({ 
                    channelId: channelCurrent?.id
                });
                isLive.value = true;
                startTimePlayerUXSec.value = 0;
                await fetchList();
                focusControls();
                try {
                    tracePlayerEventLive.stop();
                } catch (e){
                    Logger.captureLog(e as AxiosError | Error);
                }
            }
        } catch (error) {
            setTooltipeMessage(t(Strings.PLAYER_ERROR_START_LIVE_PROGRAM).toString());
            setTooltipeShowing({visible: true});
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const onGoLive = async (id: string): Promise<void> => {
        refreshActionTime();
        //TODO: need refactoring for all diveces. isTabletDevice is temporary solution
        if (itemProcessor.value?.isLive && !itemProcessor.value?.liveIsPausedOnce && !isTabletDevice)  {
            return;
        } else {
            await startLiveProgram(id);
        }
    };
    const correctedPin = async () => {
        if (pendingChannelIndex.value !== null && targetWidgetAdultControl.value === name) {
            const channelIndex = pendingChannelIndex.value;
            pendingChannelIndex.value = null;
            await switchChannel(channelIndex);
            deleteSelectedIdProgram();
            focusControls();
        } else if (isTransitionEpisode.value && transitionEpisode.value !== null) {
            void switchEpisode(transitionEpisode.value);
            deleteSelectedIdProgram();
            focusControls();
        } else if (autoTransitionNextProgram.value) {
            await getWatchedRange();
            if (player.value?.isPaused()) {
                player.value?.resume()?.catch((error) => {
                    console.error("Error resuming video at correctedPin:", error);
                });
            }
            await rewindVideo();
            setUpdateProgram(false);
            if (PlayItem.isLive(currentProgram.value)) {
                void switchLiveProgram();
            }
        } else {
            await setProgress();
            clearWatchedRange();
            deleteSelectedIdProgram();
            focusControls();
        }
        setCurrentTimes();
            
        SetIncorrectAdultPin();
        isTransitionEpisode.value = false;
        autoTransitionNextProgram.value = false;
        isRestartingStream.value = false;
    };
    const onNextEpisode = () => {
        refreshActionTime();
        goNextEpisode().catch((error) => {
            Logger.captureError(error as AxiosError | Error);
            setTooltipeMessage(t(Strings.PLAYER_ERROR_SWITCH_EPISODE).toString());
            setTooltipeShowing({visible: true});
        });
    };
    const onPrevEpisode = () => {
        refreshActionTime();
        goPrevEpisode().catch((error) => {
            Logger.captureError(error as AxiosError | Error);
            setTooltipeMessage(t(Strings.PLAYER_ERROR_SWITCH_EPISODE).toString());
            setTooltipeShowing({visible: true});
        });
    };
    const onShowSubtitles = () => {
        refreshActionTime();
        showSubtitleModal.value = !showSubtitleModal.value;
    };
    const refreshActionTime = () => {
        if (hideTimeout.value !== null) {
            clearTimeout(hideTimeout.value);
        }
        if (showControls.value) {
            let time = 5000;
            if (isVisibleDescription.value) {
                time = 10000;
            }
            hideTimeout.value = setTimeout(() => {
                showControls.value = false;
            }, time);
        }
    };
    const refreshSkipTime = (timeToSeek?: number) => {
        if (rewindTimeout.value !== null) {
            clearTimeout(rewindTimeout.value);
        }
        if (dispatchAvailabilityRange.value) {
            setWatchedRange(startTimePlayerUXSec.value, currentTimeUnix.value).catch((error) => {
                console.error("Error setting watched range at refreshSkipTime:", error);
            });
        }
        dispatchAvailabilityRange.value = false;
        const newSkipTime = timeToSeek && timeToSeek > 0 ? timeToSeek : skipPosition.value;
        const newPosTime = timeToSeek && timeToSeek > 0 ? timeToSeek : currentTimePosition.value;
        rewindTimeout.value = setTimeout(() => {
            if (player.value) {
                player.value.setCurrentTime(newSkipTime);
                startTimePlayerUXSec.value =  startTimeSec.value + newPosTime;
                dispatchAvailabilityRange.value = true;
            }
        }, 1000);
    };
    const setProgress = async (): Promise<void> => {
        if (getIsRequestIsLocked.value) {
            console.warn("setProgress is locked");
            return;
        }
        if (currentProgram.value && !playingIsChannel.value) {
            const id = currentProgram.value.recentAirTime?.id ?? currentProgram.value.id ?? 0;
            void playbackProgress({ 
                id: id, type: currentProgram.value.contentType!, progress: mediaCurrentTime.value
            });
            try {
                await setWatchedRange(startTimePlayerUXSec.value, currentTimeUnix.value);
            } catch (error) {
                console.error("Error setting watched range at setProgress:", error);
            }

            localStorage.removeItem("progressPosition");

            if (isVODSeries.value) {
                setProgressEpisodeById(id, mediaCurrentTime.value);
            }
            
            if (isVCloud.value) {
                setProgressProgramById(id, mediaCurrentTime.value);
            }
        }
    };

    const setProgressProgramById = (id: number, pos: number): void => {
        if (getProgramsProperties.value.length > 0) {
            getProgramsProperties.value.forEach((item: ContentProperties) => {
                if (item.id === id) {
                    item.streamPosition = pos;
                }
            });
        }
        
    };

    const setProgressEpisodeById = (id: number, pos: number): void => {
        let episodesProperties: ContentProperties[] = []; 
        if(_episodesPropertiesHomePage.value.length > 0) {
            episodesProperties =  _episodesPropertiesHomePage.value;
        }
        
        if(_episodesPropertiesDetailPage.value.length > 0) {
            episodesProperties =  _episodesPropertiesDetailPage.value;
        }

        if(episodesProperties.length > 0) {
            episodesProperties.forEach((item: ContentProperties) => {
                if (item.id === id) {
                    item.streamPosition = pos;
                }
            });
        }
    }

    const setProgressLocalStorage = () => {
        if (currentProgram.value && mediaCurrentTime.value !== 0) {
            const id = currentProgram.value.recentAirTime?.id ?? currentProgram.value.id;
            localStorage.setItem("progressPosition", JSON.stringify({"id": id, "type": currentProgram.value.contentType!, "progress": mediaCurrentTime.value}));
        }
    };
    const closeModal = () => {
        showSubtitleModal.value = false;
        focusControls();
    };
    const getItemProperties = async () => {
        const itemId = currentProgram.value?.recentAirTime?.id ?? currentProgram.value?.id ?? currentChannel.value?.id;
        if (!itemId) {
            // TODO: this causes an error if you leave the player before initialization
            throw new Error("Item id is not defined");
        }
        await fetchItemProperties(
            new GetItemPropertiesRequest([itemId], currentProgram.value?.contentType, [ContentItemProps.FavouriteStatee, ContentItemProps.WatchedPosition])
        );
    };
    const getItemPropertiesChannel = () => {
        if (!currentProgram.value?.recentAirTime?.channelId) {
            throw new Error(`${errorChannelId}: ${JSON.stringify(currentProgram.value)}`);
        }
        void fetchItemProperties(
            new GetItemPropertiesRequest([currentProgram.value?.recentAirTime?.channelId], ContentType.LiveTV, [ContentItemProps.FavouriteStatee, ContentItemProps.WatchedPosition])
        );
    };
    const setSubtitles = (val: Array<PlayerTracks> | null) => {
        allSubtitles.value = val;
    };
    const setAudio = (val: Array<PlayerTracks> | null) => {
        allAudio.value = val;
    };
    const updateLiveProgram = async (prevProgram: PlayItem | null, channel: Channel | null) => {
        await setProgress();
        clearWatchedRange();
        
        if (!prevProgram?.recentAirTime?.channelId) {
            throw new Error(`${errorChannelId}: ${JSON.stringify(
                {
                    programId: prevProgram?.id,
                    programName: prevProgram?.name,
                    recentAirTime: prevProgram?.recentAirTime,
                    contentType: [prevProgram?.contentType, itemContentType.value],
                    channelId: channel?.id,
                    channelName: channel?.name,
                }
            )}`);
        }

        await updateCurrentProgram({ 
            channelId: prevProgram.recentAirTime.channelId
        });

        const advisory = prevProgram?.advisory ?? channel?.advisory;
        
        startTimePlayerUXSec.value = startTimeSec.value;
        if (typeof advisory === 'number' && needsUnlockPin(advisory)) {
            setUpdateProgram(true);
            autoTransitionNextProgram.value = true;
            if (!player.value?.isPaused()) {
                player.value?.pause();
            }
            ShowParentModal();
        }
    };
    const updateVcloudProgram = async (prevProgram: PlayItem | null, channel: Channel | null) => {
        await setProgress();
        clearWatchedRange();
        
        if (!prevProgram?.recentAirTime?.channelId) {
            throw new Error(`${errorChannelId}: ${JSON.stringify(
                {
                    programId: prevProgram?.id,
                    programName: prevProgram?.name,
                    recentAirTime: prevProgram?.recentAirTime,
                    contentType: [prevProgram?.contentType, itemContentType.value],
                    channelId: channel?.id,
                    channelName: channel?.name,
                }
            )}`);
        }

        const time = prevProgram.recentAirTime.startTime! + prevProgram.lengthInS! * 1000;
        await updateCurrentProgramByTime({ 
            channelId: prevProgram.recentAirTime.channelId!,
            time
        });
        startTimePlayerUXSec.value = Math.floor(startTimeSec.value + streamPosition.value);
        const advisory = prevProgram.advisory ?? channel?.advisory;
        
        if (typeof advisory === 'number' && needsUnlockPin(advisory)) {
            setUpdateProgram(true);
            autoTransitionNextProgram.value = true;
            if (!player.value?.isPaused()) {
                player.value?.pause();
            }
            ShowParentModal();
            return;
        }
        if (hasLive.value || isRestart.value) {
            void switchLiveProgram();
            return;
        }
        await getWatchedRange();
        await rewindVideo();
    };
    const switchLiveProgram = async () => {
        try {
            isLive.value = true;
            startTimePlayerUXSec.value = Math.floor(TimeUtils.dateNow()/1000);
            isRestartingStream.value = false;
            await fetchList();
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
            setTooltipeMessage(t(Strings.PLAYER_ERROR_SWITCH_PROGRAM).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const closePlayer = () => {
        emit("exit-play");
    };
    const rewindVideo = async () => {
        const progressPosition = JSON.parse(localStorage.getItem("progressPosition")!);
        const savedProgress = progressPosition?.progress;
        await getItemProperties();
        if (savedProgress) {
            onSeek(savedProgress);
        } else if (streamPosition.value && streamPosition.value < maxDurationForResume.value) {
            onSeek(streamPosition.value);
        }
        if (streamPosition.value < maxDurationForResume.value) {
            startTimePlayerUXSec.value = Math.floor(startTimeSec.value + streamPosition.value);
        }
        startTimePlayerUXSec.value = startTimeSec.value;
    };
    const showBtnNextEpisode = () => {
        if (nextEpisode.value) {
            isVisibleBtnNextEpisode.value = true;
        }
    };
    const updateNextEpisode = () => {
        if (isEnableAutoplay.value) {
            // Prevent going to the next episode if there is no next episode
            if (!nextEpisode.value) {
                return;
            }
            goNextEpisode().catch((error) => {
                Logger.captureError(error as AxiosError | Error);
                setTooltipeMessage(t(Strings.PLAYER_ERROR_SWITCH_EPISODE).toString());
                setTooltipeShowing({visible: true});
            });

            if(!showControls.value){
                focusCatcher.value?.focus();
            }
        } else {
            isVisibleBtnShowReplay.value = true;
        }
    };
    const goNextEpisode = async (): Promise<void> => {
        if (!nextEpisode.value) {
            // Show a tooltip if the next episode is not available and the Next Episode button on the remote control is pressed
            showFunctionUnavailableTooltip();
            return;
        }
        await setProgress();
        clearWatchedRange();
        if (typeof currentProgram.value?.advisory === 'number' && needsUnlockPin(currentProgram.value?.advisory)) {
            transitionEpisode.value = nextEpisode.value;
            isTransitionEpisode.value = true;
            ShowParentModal();
            let id = "btn-nav-forward";
            if (isVisibleBtnNextEpisode.value && !showControls.value) {
                id = BtnsIdsEpisodeEndScreen.btnNextEpisode;
            }
            addSelectedIdProgram(id);
            return;
        }
        void switchEpisode(nextEpisode.value);
    };
    const goPrevEpisode = async () => {
        if (!prevEpisode.value) {
            showFunctionUnavailableTooltip();
            return;
        }
        await setProgress();
        clearWatchedRange();
        if (typeof currentProgram.value?.advisory === 'number' && needsUnlockPin(currentProgram.value?.advisory)) {
            transitionEpisode.value = prevEpisode.value;
            isTransitionEpisode.value = true;
            const id = "btn-nav-back";
            ShowParentModal();
            addSelectedIdProgram(id);
            return;
        }
        void switchEpisode(prevEpisode.value);
    };
    const switchEpisode = async(val: PlayItem) => {
        try {
            isVisibleBtnNextEpisode.value = false;
            isVisibleBtnShowReplay.value = false;

            startPlayingProgram(val);
            if (streamPosition.value < maxDurationForResume.value) {
                startTimePlayerUXSec.value = Math.floor(startTimeSec.value + streamPosition.value);
            }
            startTimePlayerUXSec.value = startTimeSec.value;
            await fetchList();
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
            setTooltipeMessage(t(Strings.PLAYER_ERROR_SWITCH_EPISODE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const replay = () => {
        if (player.value) {
            void setProgress();
            isVisibleBtnNextEpisode.value = false;
            isVisibleBtnShowReplay.value = false;
            startTimePlayerUXSec.value = startTimeSec.value;
            player.value.setCurrentTime(0);
            if (player.value?.isPaused()) {
                player.value?.resume()?.catch((error) => {
                    console.error("Error resuming video at replay:", error);
                });
            }
        }
    };
    const clickBtnsReplay = () => {
        const focusId = document.activeElement?.id;
        if (focusId === 'replay') {
            replay();
        }
        goNextEpisode().catch((error) => {
            Logger.captureError(error as AxiosError | Error);
            setTooltipeMessage(t(Strings.PLAYER_ERROR_SWITCH_EPISODE).toString());
            setTooltipeShowing({visible: true});
        });
    };
    const showTooltypeGoLive = () => {
        setTooltipeMessage(t(Strings.PLAYER_PROGRAM_ENDED).toString());
        setTooltipeShowing({visible: true, showingTime: AppConstants.durationForTooltipeMessageProgramEnded * 1000});
    };
    const registerKeyListeners = () => {
        if (!isRegKeyBackListeners.value) {
            $navigator.on("keypress:back", keyBackListener);
            isRegKeyBackListeners.value = true;
        }
    };
    const unregisterKeyListeners = () => {
        if (isRegKeyBackListeners.value) {
            $navigator.off("keypress:back", keyBackListener);
            isRegKeyBackListeners.value = false;
        }
    };
    const keyBackListener = () => {
        resetLoader();
        closePlayer();
    };  
    const itemsSelected = async (val: {lineId: string; item: ChannelLiveCardData | ProgramCardData | OnDemandCardData | EpisodeCardData}) => {
        focusControls();
        clearWatchedRange();
        switch (val.lineId) {
            case AppConstants.shelfKeys.playerChannelsCards:
                await onPlayChannel(val.item as ChannelLiveCardData);
                break;

            case AppConstants.shelfKeys.playerEpisodesCards:
                await onPlayCard(val.item as EpisodeCardData);
                break;

            case AppConstants.shelfKeys.playerRelatedCards:
                await onPlayCard(val.item as OnDemandCardData);
                break;

            default:
                break;
        }
    };

    const updateRecentChannels = (idChannel: number) => {
        fetchRecentChannelsForHomePage().then(() => {
            const lineId = getCurrentSelectedIdProgram.value?.split('_')[0];
            if (lineId !== AppConstants.shelfKeys.recentChannelsCards) {
                return;
            }

            const newSelectedIdProgram = [lineId, idChannel].join('_');
            deleteSelectedIdProgram();
            addSelectedIdProgram(newSelectedIdProgram);
        }).catch((error) => {
            Logger.captureError(error as AxiosError | Error);
        });
    };

    const onPlayChannel = async (item: ChannelLiveCardData) => {
        try {
            const program = await $services
                .guide
                .getChannelProgramByTime(Number(item.getId()), TimeUtils.dateNow());
            if (!program) {
                throw new Error(`Program is not found in channel at onPlayChannel: ${JSON.stringify(
                    {
                        channelId: item.getId(),
                        currentChannelId: currentChannel.value?.id,
                        currentChannelName: currentChannel.value?.name,
                        currentProgramName: currentProgram.value?.name,
                        currentRecentAirTime: currentProgram.value?.recentAirTime,
                        currentContentType: [currentProgram.value?.contentType, itemContentType.value],
                    }
                )}`);
            }

            startPlayingChannel(item, false).then( () => {
                player.value?.dispose().then(async () => {
                    isLive.value = true;
                    void router.push(linkItems.player.routerPath);
                    
                    await fetchList();

                    startTimePlayerUXSec.value = Math.floor(Date.now()/1000);
                    isRestartingStream.value = false;
                    
                    // TODO: wrap updateRecentChannels into condition: if back to home page; or remove it
                    updateRecentChannels(Number(item.getId())); 
                }).catch((error) => {
                    throw error;
                });
            }).catch((error) => {
                throw error;
            });
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
            setTooltipeMessage(t(Strings.PLAYER_ERROR_SWITCH_CHANNEL).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const onPlayCard = async (card: EpisodeCardData | OnDemandCardData) => {
        try {
            await setProgress();
            isVisibleBtnNextEpisode.value = false;
            isVisibleBtnShowReplay.value = false;
            const playItem = card.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(card));
            }
            startPlayingProgram(playItem);
            setViewedDetailItems(playItem);
            if (streamPosition.value < maxDurationForResume.value) {
                startTimePlayerUXSec.value = Math.floor(startTimeSec.value + streamPosition.value);
            }

            if (isVODSeries.value) {
                if (!getEpisodesForHomePage.value?.items?.length && !getEpisodesForDetailPage.value?.items?.length) {
                    await fetchEpisodes(playItem);
                }
            }
            if (isVODMovies.value) {
                await fetchRelatedContent(playItem);
            }
            startTimePlayerUXSec.value = startTimeSec.value;
            await fetchList();
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
            setTooltipeMessage(t(Strings.PLAYER_ERROR_SWITCH_PROGRAM).toString());
            setTooltipeShowing({visible: true});
        }
    };

    const onWaiting = () => {
        isWaitingLoader.value = true;
        waitingTimer = setTimeout(() => {
            showBufferingTooltip();
        }, slowInternetTimeout);
    };

    const onCanplay = () => {
        clearWaiting();
    };

    const clearWaiting = () => {
        isWaitingLoader.value = false;
        if (waitingTimer) {
            clearTimeout(waitingTimer);
        }
        hideBufferingTooltip();
    };

    const showBufferingTooltip = () => {
        setTooltipeMessage(t(Strings.PLAYER_BUFFERING).toString());
        setTooltipeShowing({visible: true, showingTime: 180000});
    };

    const hideBufferingTooltip = () => {
        setTooltipeShowing({visible: false, showingTime: 0});
    };

    const getPreferredAudio = () => {
        const itemLocalStorage = JSON.parse(localStorage.getItem("PreferredAudio")!);
        preferredAudio.value = itemLocalStorage ? itemLocalStorage.preferredAudio : '';
    };

    const setPreferredAudio = (audioId: string) => {
        localStorage.setItem("PreferredAudio", JSON.stringify({"preferredAudio": audioId}));
    };

    const resetLoader = () => {
        isWaitingLoader.value = false;
        isStarted.value = true;
    };

    const hardRestart = () => {
        if (player.value && !isLive.value) {// for live stream, we don't need to trigger startPlaying as so as we re-fetch the stream in the offline watcher
            increasePlayerKey();
            void nextTick(() => {
                void nextTick(() => {
                    player.value?.setCurrentTime(mediaCurrentTime.value);
                });
            });
        }
    };

    const onClickPlayer = (event: Event) => {
        emit("emulateKeyEnter", event);
    };

    const hoverOnTime = (time: number) => {
        hoveredTime.value = time;
    };

    const createAnalytics = () => {
        const vodCurrentGenre = getTopGenres.value?.find((item) => item.id === currentProgram.value?.topGenreId);
        const genreId = isVOD.value ? currentProgram.value?.topGenreId : currentProgram.value?.recentAirTime?.channelId;
        const genreName = isVOD.value ? vodCurrentGenre?.name : currentChannel.value?.name;
        const programId = currentProgram.value?.recentAirTime?.id ?? currentProgram.value?.id ?? currentChannel.value?.id;
        
        $analytics.createCustomScreenEvent({
            [AnalyticsParam.FA_SCREEN_NAME]: playerName.value!,
            [AnalyticsParam.FA_SCREEN_CLASS]: playerName.value,
            [AnalyticsParam.ITEM_ID]: programId ? String(programId) : null,
            [AnalyticsParam.CONTENT_TYPE]: currentProgram.value?.contentType,
            [AnalyticsParam.CHANNEL_ID]: genreId ? String(genreId) : null,
            [AnalyticsParam.CHANNEL_NAME]: genreName,
            [AnalyticsParam.IS_SERIES]: isVODSeries.value,
            [AnalyticsParam.MOVIE_NAME]: isVODMovies.value ? currentProgram.value?.name : null,
            [AnalyticsParam.SERIES_NAME]: isVODSeries.value ? currentProgram.value?.seriesName : null,
            [AnalyticsParam.EPISODE_NAME]: isVODSeries.value ? currentProgram.value?.name : null,
            [AnalyticsParam.PROGRAM_NAME]: !isVOD.value ? currentProgram.value?.name : null,
        })
    };

    const findChannelSegmentByChannel = (idChannel: number) => {
        if(!idChannel){
            Logger.captureError(new Error("Channel ID is missing"));
            return;
        }

        const channelIdx = playerChannels.value.findIndex((channel) => channel.id === idChannel);
        if (channelIdx === indexCurrentChannel.value) {
            return;
        }

        if (isLoaded.value) {
            refreshActionTime();
            void checkChannelParentalControl(channelIdx);
        }
    };

    watch(
        mediaCurrentTime,
        (value: number): void => {
            if (playingIsChannel.value) {
                return;
            }

            //'autoplay = on' for restarting stream
            if (isRestart.value) {
                if ((startTimeSec.value + value) === currentProgramEndTimeSec.value - AppConstants.durationForTooltipeMessageProgramEnded) {
                    showTooltypeGoLive();
                }
                if ((startTimeSec.value + value) >= currentProgramEndTimeSec.value || (vend.value > 0 && (startTimeSec.value + value) >= vend.value)) {
                    void startLiveProgram();
                }
            }

            //'autoplay = on' for live
            if (hasLive.value) {
                if (vend.value > 0 && (startTimeSec.value + value) >= vend.value) {
                    void startLiveProgram();
                } else if ((startTimeSec.value + value) >= currentProgramEndTimeSec.value) {
                    updateLiveProgram(currentProgram.value, currentChannel.value).catch((error) => {
                        Logger.captureError(error as AxiosError | Error);
                        setTooltipeMessage(t(Strings.PLAYER_ERROR_SWITCH_PROGRAM).toString());
                        setTooltipeShowing({visible: true});
                    });
                }
            }

            //'autoplay = on' for vcloud
            if (isVCloud.value && (startTimeSec.value + value) >= currentProgramEndTimeSec.value) {
                if(isEnableAutoplay.value) {
                    updateVcloudProgram(currentProgram.value, currentChannel.value).catch((error) => {
                        Logger.captureError(error as AxiosError | Error);
                        setTooltipeMessage(t(Strings.PLAYER_ERROR_SWITCH_PROGRAM).toString());
                        setTooltipeShowing({visible: true});
                    });
                } else {
                    onPlay();
                }
            } 
            
            //'autoplay = on' for VOD series
            if (isVODSeries.value) {
                if (value >= startimeForEpisodeEndScreen.value) {
                    showBtnNextEpisode();
                } else {
                    isVisibleBtnNextEpisode.value = false;
                    isVisibleBtnShowReplay.value = false;
                }
            } 
        }
    );
    watch(
        getIsPlayerMaintenanceModalShow,
        (value: boolean) => {
            if (value) {
                showMaintenanceModal();
            }
        }
    );

    watch(
        getIs507ErrorModalShow,
        (value: boolean) => {
            if (value) {
                show507ErrorModal();
            }
        }
    );

    watch(
        isPlaying,
        (val: boolean) => {
            if (val) {
                if (!playingIsChannel.value) {
                    handle.value = setInterval(() => {
                        void setProgress();
                    }, 300000);
                    
                    handleLocalStorage.value = setInterval(() => {
                        if (player.value !== null && !playingIsChannel.value) {
                            setProgressLocalStorage();
                        }
                    }, 5000);
                    void fetchNpasMessage();
                    handleNpasMessage.value = setInterval(() => {
                        void fetchNpasMessage();
                    }, 300000);
                }
            } else {
                clearTimerStreamProgress();
            }
        }
    );
    watch(
        mediaSource,
        (val: string | null) => {
            if (val) {
                void getBif(convertSrcToBif(val));
            }
        }
    );
    watch(
        showSubtitleModal,
        (value: boolean) => {
            if (value) {
                if (hideTimeout.value) {
                    clearTimeout(hideTimeout.value!);
                }
                isModalClosed.value = true;
            } else {
                refreshActionTime();
                isModalClosed.value = false;
            }
            emit("showSubtitleModal",showSubtitleModal.value);
        }
    );
    watch(
        isTypeModeEnabledPin,
        (value: boolean) => {
            if (value) {
                if (hideTimeout.value) {
                    clearTimeout(hideTimeout.value!);
                }
                isModalClosed.value = true;
            } else {
                refreshActionTime();
                isModalClosed.value = false;
                transitionEpisode.value = null;
                isTransitionEpisode.value = false;
                pendingChannelIndex.value = null;
            }
        }
    );
    watch(
         isLoaded,
         async (val: boolean) => {
            if (val && streamPosition.value && streamPosition.value < maxDurationForResume.value && player.value && !hasLive.value) {
                player.value!.setCurrentTime(streamPosition.value);
            }
            startTimePlayerUXSec.value = startTimeSec.value;
            if (streamPosition.value && streamPosition.value < maxDurationForResume.value) {
                startTimePlayerUXSec.value = Math.floor(startTimeSec.value + streamPosition.value);
            }
            if (hasLive.value) {
                startTimePlayerUXSec.value = Math.floor(TimeUtils.dateNow()/1000);
            }
            if (val && tracePlayer) {
                try {
                    await tracePlayer.stop();
                    Logger.captureLog(`PLAYER stop ${tracePlayer.durationUs / 1000000} sec`);
                    tracePlayer = null;
                } catch (e) {
                    Logger.captureLog(e);
                }
            }
        }
    );

    watch(
        isAdultPinCorrect,
        (value: boolean) => {
            if (value) {
                correctedPin().catch((error) => {
                    Logger.captureError(error as AxiosError | Error);
                });
            }
        }
    );
    watch(
        isPlayerClosure,
        (value: boolean) => {
            if (value) {
                closePlayer();
            }
        }
    );
    watch(
        isOffline,
        async (val: boolean) => {
            if (!val) {
                if (isLive.value) {
                    try {
                        if(!currentChannel.value?.id) {
                            throw new Error(`${errorChannelId}: ${currentChannel.value?.id}`);
                        }
                        await updateCurrentProgram({ 
                            channelId: currentChannel.value?.id
                        });
                        await fetchList();
                    } catch (error) {
                        Logger.captureError(error as AxiosError | Error);
                        setTooltipeMessage(t(Strings.PLAYER_ERROR_RECONNECTION).toString());
                        setTooltipeShowing({visible: true});
                    }
                }
            }
        }
    );
    watch(
        isVisibleBtnNextEpisode,
        (val: boolean) => {
            emit("setVisibilityToBtnNextEpisode", val);
        }
    );
    watch(
        isAlertModalVisible,
        (val: boolean) => {
            returningFocus(val);
        }
    );
    watch(
        showControls,
        (val: boolean) => {
            emit("showControls", val);
            returningFocus(val);
        }
    );
    watch(
        isVisibleBtnShowReplay,
        (val: boolean) => {
            emit("setVisibilityToBtnShowReplay", val);
            
            if (val) {
                if (!isTypeModeEnabledPin.value) {
                    setTimeout(() => {
                        if(episodeEndScreenRef.value){
                            episodeEndScreenRef.value.focusToBtnReplay();
                        }
                    }, 125);
                }
            }
        }
    );
    watch(
        alertsRetrieved,
        (val: UserAlert[]) => {
            if (val.length > 0 && player.value) {
                setTimeout(() => {
                    if (alertsRetrieved.value.length > 0 && player.value) {
                        if (!player.value?.isPaused()) {
                            player.value?.pause();
                        }
                        setIsShownAlertModal(true);
                    }
                }, 5000);
            }

        }
    );
    watch(
        videoResume,
        (val: boolean) => {
            if (val && player.value?.isPaused()) {
                player.value?.resume()?.catch((error) => {
                    console.error("Error resuming video at watch videoResume:", error);
                });
                setVideoResume(false);
            }
        }
    );

    onBeforeMount(() => {
        try {
            tracePlayer?.start();
            Logger.captureLog('PLAYER start');
        } catch (e){
            Logger.captureLog(e);
        }
        fetchList().catch((error) => {
            Logger.captureError(error as AxiosError | Error);
        });
        setSubtitlesStores();
        setAudioStores();
        setVisibleDescription();
    });

    onMounted(() => {
        void nextTick(() => {
            if (isPlayChannel.value){
                void fetchFavChannelsIds();
            }
            focusCatcher.value?.focus();
            createAnalytics();
        });
        setPlayerAvailability(true);
        getPreferredAudio();
    });

    onBeforeUnmount(() => {
        if (!playingIsChannel.value) {
            void setProgress();
        }

        if (!isLoaded.value && tracePlayer) {
            try {
                tracePlayer.stop();
                Logger.captureLog(`beforeUnmount PLAYER stop ${tracePlayer.durationUs / 1000000} sec`);
                tracePlayer = null;
            } catch (e) {
                Logger.captureLog(e);
            }
        }
        unregisterKeyListeners();
    });

    onUnmounted(() => {
        if (hideTimeout.value) {
            clearTimeout(hideTimeout.value!);
        }
        if (handleNpasMessage.value) {
            clearInterval(handleNpasMessage.value!);
        }
        if (rewindTimeout.value) {
            clearTimeout(rewindTimeout.value!);
        }

        clearTimerStreamProgress();

        setPlayerClosure(false);
        clearWatchedRange();
        clearWaiting();
        setToInitialState();
    });


    defineExpose<ExposeInterface>({
        clickBtnsReplay,
        toggleShowControls,
        onHide,
        goNextEpisode,
        goPrevEpisode,
        onRewind,
        onPlayPressed,
        onPausePressed,
        onFastForward,
        onLeftPressed,
        onRightPressed,
        onNextChannelPressed,
        onPrevChannelPressed,
        confirmStop,
        onPlay,
    });
</script>
